import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { roleLabel } from "@/lib/roles";
import { ShieldAlert, Share2, UserCircle } from "lucide-react";
import { urlBase64ToUint8Array, isIOS, isStandalone } from "@/lib/pwa";
import { CodeExportCard } from "@/components/CodeExportCard";

/** Perfil del usuario -- Prompt Emergent Y, CAMBIO 4: interruptor de alertas
 * de pánico push en este dispositivo. El permiso del navegador se pide
 * SOLO cuando el usuario toca el interruptor, nunca al entrar a la página. */
export default function Profile() {
  const { user } = useAuth();
  const [state, setState] = useState("checking"); // checking | unsupported | blocked | disabled | enabled
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);

  const refreshState = async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || typeof Notification === "undefined") {
        setState("unsupported");
        return;
      }
      if (Notification.permission === "denied") {
        setState("blocked");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setState(sub ? "enabled" : "disabled");
    } catch {
      setState("unsupported");
    }
  };

  useEffect(() => { refreshState(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") { await refreshState(); return; }
      const { data } = await api.get("/push/public-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      const subJson = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: subJson.endpoint, keys: subJson.keys, user_agent: navigator.userAgent });
      toast.success("Alertas de pánico activadas en este dispositivo");
      await refreshState();
    } catch (e) { toast.error(formatApiError(e)); await refreshState(); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete("/push/unsubscribe", { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      toast.success("Alertas de pánico desactivadas en este dispositivo");
      await refreshState();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    setTestBusy(true);
    try {
      const { data } = await api.post("/push/test");
      toast.success(`Prueba enviada a ${data.sent} dispositivo(s)`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTestBusy(false); }
  };

  return (
    <div style={{ padding: "20px 24px 60px", maxWidth: 640 }} data-testid="profile-page">
      <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase" }}>Cuenta</div>
      <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 18px", color: "#0A0A0A" }}>Mi perfil</h1>

      <div className="card" style={{ padding: 16, marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }} data-testid="profile-account-card">
        <UserCircle size={30} style={{ color: "var(--invict-text-muted)" }} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#0A0A0A" }}>{user?.name}</div>
          <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>{user?.email} · {roleLabel(user?.role)}</div>
        </div>
      </div>

      <div className="card" style={{ padding: 16 }} data-testid="profile-push-card">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <ShieldAlert size={18} style={{ color: "#BF3437" }} />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#0A0A0A" }}>Alertas de pánico en este dispositivo</h3>
        </div>
        <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "0 0 14px" }}>
          Recibí una notificación cuando se dispare un SOS, incluso con la app cerrada. Solo se envían alertas de pánico — nada de rondas, chats ni novedades.
        </p>

        {state === "checking" && <div className="skel" style={{ height: 40 }} />}

        {state === "unsupported" && (
          <div data-testid="profile-push-unsupported" style={{ fontSize: 12, background: "var(--invict-bone-2)", padding: 12, borderRadius: 2, color: "#0A0A0A" }}>
            Este navegador no soporta notificaciones push.
            {isIOS() && !isStandalone() && (
              <div style={{ marginTop: 8 }}>
                <Share2 size={12} style={{ marginRight: 4, display: "inline", verticalAlign: "text-top" }} />
                En iPhone: tocá <b>Compartir → Agregar a pantalla de inicio</b> primero. Sin instalar la app así, iOS no entrega notificaciones (necesitás iOS 16.4 o superior).
              </div>
            )}
          </div>
        )}

        {state === "blocked" && (
          <div data-testid="profile-push-blocked" style={{ fontSize: 12, background: "rgba(191,52,55,0.08)", border: "1.5px solid rgba(191,52,55,0.25)", padding: 12, borderRadius: 2, color: "#0A0A0A" }}>
            <b style={{ color: "#BF3437" }}>Bloqueadas.</b> Ya rechazaste el permiso y el navegador no lo vuelve a preguntar. Para activarlas: entrá a la configuración del sitio en tu teléfono (ícono de candado junto a la dirección, o Configuración → Apps → Chrome → Permisos → Notificaciones) y habilitá las notificaciones para esta app.
          </div>
        )}

        {(state === "enabled" || state === "disabled") && (
          <>
            <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, cursor: busy ? "default" : "pointer" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#0A0A0A" }}>{state === "enabled" ? "Activadas en este dispositivo" : "Desactivadas"}</span>
              <input
                type="checkbox" checked={state === "enabled"} disabled={busy}
                onChange={() => (state === "enabled" ? disable() : enable())}
                data-testid="profile-push-toggle" style={{ width: 20, height: 20, cursor: "pointer" }}
              />
            </label>
            {state === "enabled" && (
              <button className="btn-ghost" onClick={sendTest} disabled={testBusy} style={{ marginTop: 12, fontSize: 11, padding: "8px 14px" }} data-testid="profile-push-test-button">
                {testBusy ? "Enviando..." : "Enviar notificación de prueba"}
              </button>
            )}
          </>
        )}
      </div>
      <CodeExportCard />
    </div>
  );
}
